import React from "react";
import { Box, Typography, Theme, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { AppLogo } from "../app/AppLogo";
import { Avatar } from "../avatar/Avatar";
import logo from "../app/logo.svg";
import { LibraryPrivacyType } from "../../constants";

interface StyleProps {
  size: number;
  badgePosition?: "top-right" | "bottom-right";
  avatarRadius?: number;
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  root: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  avatar: {
    width: (props) => props.size,
    height: (props) => props.size,
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.border.primary}`,
    margin: 0,
  },
  avatarBadge: {
    position: "absolute",
    top: 0,
    right: 0,
    transform: "translate(35%, -35%)",
    borderRadius: "50%",
    border: `1px solid ${theme.palette.border.primary}`,
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    boxShadow: theme.shadows[1],
    margin: 0,
  },
  logoContainer: {
    position: "relative",
    display: "inline-flex",
  },
  typeLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
}));

export interface LibraryPrivacyTypeBadgeProps {
  type: LibraryPrivacyType;
  owner?: {
    photoUrl?: string;
    displayName?: string;
  };
  tooltip?: string;
  size?: number;
  showLabel?: boolean;
  badgePosition?: "top-right" | "bottom-right";
}

export const privacyTooltip = (
  itemText: string,
  type: LibraryPrivacyType,
  ownerName?: string,
) => {
  switch (type) {
    case LibraryPrivacyType.PUBLIC:
      return `Public ${itemText} available to all users`;

    case LibraryPrivacyType.PRIVATE:
      return `Private ${itemText} created by you`;

    case LibraryPrivacyType.TEAM:
      return ownerName
        ? `Team ${itemText} shared within your workspace, created by ${ownerName}`
        : `Team ${itemText} shared within your workspace`;
  }
};

const getTypeLabel = (type: LibraryPrivacyType): string => {
  switch (type) {
    case LibraryPrivacyType.PUBLIC:
      return "Public";
    case LibraryPrivacyType.PRIVATE:
      return "Private";
    case LibraryPrivacyType.TEAM:
      return "Team";
    default:
      return "Unknown";
  }
};

export const GenericTemplateTypeBadge = ({
  type,
  owner,
  tooltip,
  size = 40,
  showLabel = false,
  badgePosition = "top-right",
}: LibraryPrivacyTypeBadgeProps) => {
  const s = useStyles({ size, badgePosition });

  const content = (
    <Box className={s.root}>
      {type === LibraryPrivacyType.PUBLIC && (
        <img src={logo} alt="Public" width={size} height={size} />
      )}

      {type === LibraryPrivacyType.PRIVATE && (
        <Avatar
          photoURL={owner?.photoUrl}
          displayName={owner?.displayName}
          className={s.avatar}
        />
      )}

      {type === LibraryPrivacyType.TEAM && (
        <Box className={s.logoContainer}>
          <AppLogo
            size={size}
            width={size}
            height={size}
            className={s.avatar}
          />
          {owner && (
            <Avatar
              photoURL={owner.photoUrl}
              displayName={owner.displayName}
              className={s.avatarBadge}
            />
          )}
        </Box>
      )}

      {showLabel && (
        <Typography className={s.label}>{getTypeLabel(type)}</Typography>
      )}
    </Box>
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
};
